/**!
 * Created by Leonardo Cheslacki on 16/01/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import HelpersController from './../HelpersController';

let _this = {};

/**
 *
 */
class StagesViews extends HelpersController {
    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main) {
        super(props);

        this.props = props;
        this.main = main;

        this.state = {
            data: [],
            bitmap: null
        }

        _this = this;
    };

    /**
     *
     * @returns {*}
     */
    get = () => {
        const {current_name} = this.props;

        return this.main.getState(`_this_${current_name}`);
    };

    /**
     *
     * @returns {string}
     */
    occurrence = () => {
        const {currentFrame} = this.get();

        return this.padWithLeadingZeros(currentFrame, 2);
    };

    /**
     *
     * @returns {string|*}
     */
    getFrame = () => {
        return this.verifyField(this.get(), `occ_frame_${this.occurrence()}`);
    };

    /**
     *
     * @param event
     * @returns {`frame${string}`}
     */
    getLabel = (event) => {
        let {name} = event.target;

        return `frame${this.padWithLeadingZeros(name.substring(name.length - 2), 2)}`;
    };

    /**
     *
     * @param _id
     * @returns {object}
     */
    getPath = (_id) => {
        const {data} = this.state;

        let array = data.filter((value) => {
            return (this.verifyField(value, '_id.#text', false, 0).toString() === _id.toString());
        });

        return this.isArrayIf(array) ? array[0] : {};
    };

    /**
     *
     * @param path
     */
    addChild = (path) => {
        const _this_current = this.get();

        const image = (new Image());

        image.src = `${this.main.state.uri}/${path}`;

        this.state.bitmap && _this_current.occ_view.removeChild(this.state.bitmap);

        this.state.bitmap = (new this.main.$createJs.Bitmap(image));

        _this_current.occ_view.addChild(this.state.bitmap);

        this.main.effectFadeIn(_this_current, 'occ_view');
    };

    /**
     *
     */
    getData = () => {
        const {name, source} = this.props;

        this.main.getXml(`${source}/${name}.xml`, (response) => {
            this.state.data = this.verifyField(response, 'products.product', false, []);

            let path = this.verifyField(this.getPath('0101'), 'path.#text');

            path && this.addChild(path);
        });
    };

    /**
     *
     * @param frame
     */
    navigateView = (frame) => {
        let _this_frame = this.getFrame();

        _this_frame && _this_frame.gotoAndStop(frame);

        let path = this.verifyField(this.getPath(`${this.occurrence()}${this.padWithLeadingZeros(frame.substring(frame.length - 2), 2)}`), 'path.#text');

        path && this.addChild(path);
    };

    /**
     *
     * @param frame
     */
    navigateMain = (frame) => {
        const
            {default_frame} = this.props,
            _this_current = this.get();

        _this_current.gotoAndStop(frame);

        const _this_frame = this.getFrame();

        if (_this_frame) {
            const {totalFrames} = _this_frame;

            _this_frame && _this_frame.gotoAndStop(default_frame);

            /**
             *
             *  Add event in button for paginate in frame.
             */
            for (let i = 0; i < (totalFrames - 1); i++) {
                let occurrence = this.padWithLeadingZeros((i + 1), 2);

                this.removeListener('click', `show-view-${occurrence}`, this.paginateView, () => {
                    this.addListener(_this_frame[`btn_view_frame_${occurrence}`], 'click', `show-view-${occurrence}`, this.paginateView);
                });
            }

            let path = this.verifyField(this.getPath(`${this.occurrence()}01`), 'path.#text');

            path && this.addChild(path);
        }
    };

    /**
     *
     */
    first = () => {
        const {default_frame} = this.props;

        this.navigateMain(default_frame);

        this.getData();
    };

    /**
     *
     * @param event
     */
    paginateView = (event) => {
        this.navigateView(this.getLabel(event));
    };

    /**
     *
     * @param event
     */
    paginateMain = (event) => {
        this.navigateMain(this.getLabel(event));
    };

    /**
     *
     */
    create = () => {
        const
            _this_current = this.get(),
            {totalFrames} = _this_current;

        this.first();

        /**
         *
         *  Add event in button for paginate in frame.
         */
        for (let i = 0; i < (totalFrames - 1); i++) {
            let occurrence = this.padWithLeadingZeros((i + 1), 2);

            this.removeListener('click', `show-main-${occurrence}`, this.paginateMain, () => {
                this.addListener(_this_current[`btn_main_frame_${occurrence}`], 'click', `show-main-${occurrence}`, this.paginateMain);
            });
        }
    };
}

/**
 *
 * @type {{default_frame: Requireable<string>, library: Requireable<string>, aliases: Requireable<any[]>, name: Requireable<string>, current_name: Requireable<string>, source: Requireable<string>}}
 */
const propTypes = {
    name: PropTypes.string,
    current_name: PropTypes.string,
    default_frame: PropTypes.string,
    library: PropTypes.string,
    aliases: PropTypes.array,
    source: PropTypes.string
};

/**
 *
 * @type {{default: {default_frame: string, library: null, name: string, current_name: null, source: null}}}
 */
const config = {
    default: {
        name: 'views',
        current_name: null,
        default_frame: 'frame01',
        library: null,
        source: null
    }
};

/**
 *
 * @param _this
 * @returns {function(*): StagesViews}
 */
export default (_this) => (props) => {
    const
        {current_name} = props,
        {parent: {canvas: {baseURI}}} = _this.getState(`_this_${current_name}`);

    const defaultProps = {...config.default, ...props, source: _this.toSrc(baseURI)};
    PropTypes.checkPropTypes(propTypes, defaultProps, 'prop', 'StagesViews');
    return (new StagesViews(defaultProps, _this));
};